import React from 'react';
import { buildSmoothSvgPath } from './boardSmoothPath';

export const isRenderableBoardShape = shape =>
  shape?.type === 'circle' ||
  shape?.type === 'rect' ||
  shape?.type === 'diamond' ||
  shape?.type === 'triangle' ||
  shape?.type === 'star' ||
  shape?.type === 'path';

export const BoardShapeSvgElement = ({
  shape,
  offsetX = 0,
  offsetY = 0,
  isSelected = false,
  interactive = false,
  preview = false,
  onPointerDown,
  onClick,
  onDoubleClick,
  dataDrawShapeId,
  dataDrawShapeRoot = false,
  dataMapShapeRoot = false,
  dataMapNodeId,
  dataTestId,
  keySuffix = '',
}) => {
  if (!shape || !isRenderableBoardShape(shape)) {
    return null;
  }

  const x = (Number(shape?.x) || 0) + offsetX;
  const y = (Number(shape?.y) || 0) + offsetY;
  const w = Math.max(0, Number(shape?.w) || 0);
  const h = Math.max(0, Number(shape?.h) || 0);
  const strokeToken = preview
    ? 'secondary'
    : isSelected
      ? 'secondary'
      : shape?.stroke || 'secondary';
  const fillToken = shape?.stroke || 'secondary';
  const strokeWidth = preview
    ? 2
    : isSelected
      ? Math.max(3, Number(shape?.strokeWidth) || 2)
      : Number(shape?.strokeWidth) || 2;
  const fillOpacity = preview
    ? 0.08
    : Number.isFinite(shape?.fillOpacity)
      ? shape.fillOpacity
      : 0;
  const commonProps = {
    key: `${shape?.id || `${x}-${y}-${w}-${h}`}${keySuffix}`,
    fill: `rgb(var(--${fillToken}))`,
    fillOpacity,
    stroke: `rgb(var(--${strokeToken}))`,
    strokeWidth,
    strokeDasharray: preview
      ? '4 3'
      : shape?.strokeDasharray || undefined,
    strokeOpacity: preview ? 0.95 : undefined,
    pointerEvents: interactive ? 'all' : 'none',
    style: interactive ? { cursor: 'pointer' } : undefined,
    onPointerDown,
    onClick,
    onDoubleClick,
    ...(dataDrawShapeId ? { 'data-draw-shape-id': dataDrawShapeId } : {}),
    ...(dataDrawShapeRoot ? { 'data-draw-shape-root': 'true' } : {}),
    ...(dataMapShapeRoot ? { 'data-map-shape-root': 'true' } : {}),
    ...(dataMapNodeId ? { 'data-map-node-id': dataMapNodeId } : {}),
    ...(dataTestId ? { 'data-testid': dataTestId } : {}),
  };

  if (shape?.type === 'path') {
    const pathD = buildSmoothSvgPath(
      Array.isArray(shape?.points)
        ? shape.points.map(point => ({
            x: (Number(point?.x) || 0) + offsetX,
            y: (Number(point?.y) || 0) + offsetY,
          }))
        : [],
    );
    if (!pathD) {
      return null;
    }
    return (
      <path
        {...commonProps}
        d={pathD}
        fill='none'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    );
  }

  if (shape?.type === 'rect') {
    return <rect {...commonProps} x={x} y={y} width={w} height={h} />;
  }

  if (shape?.type === 'diamond') {
    return (
      <polygon
        {...commonProps}
        points={`${x + w / 2},${y} ${x + w},${y + h / 2} ${x + w / 2},${y + h} ${x},${y + h / 2}`}
      />
    );
  }

  if (shape?.type === 'triangle') {
    return (
      <polygon
        {...commonProps}
        points={`${x + w / 2},${y} ${x + w},${y + h} ${x},${y + h}`}
      />
    );
  }

  if (shape?.type === 'star') {
    const outerRadius = Math.max(0, Math.min(w, h) / 2);
    const innerRadius = outerRadius * 0.45;
    const cx = x + w / 2;
    const cy = y + h / 2;
    const points = Array.from({ length: 10 }, (_, index) => {
      const angle = -Math.PI / 2 + (index * Math.PI) / 5;
      const radius = index % 2 === 0 ? outerRadius : innerRadius;
      return `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`;
    }).join(' ');
    return <polygon {...commonProps} points={points} />;
  }

  return (
    <ellipse
      {...commonProps}
      cx={x + w / 2}
      cy={y + h / 2}
      rx={Math.max(0, w / 2)}
      ry={Math.max(0, h / 2)}
    />
  );
};
